import { createAuthSlice, getTokenExpiry as foundationGetTokenExpiry } from '@foundation-bb/foundation-auth';
import { env } from '@/env';

// ---------------------------------------------------------------------------
// Cookie-based auth (Foundation pattern). The refresh token lives in an HttpOnly
// cookie the browser sends automatically; JavaScript never reads/stores it.
//
// This slice is built from Foundation's `createAuthSlice`. The previous bespoke
// clone (decodeJwtPayload/getTokenExpiry/doRefresh + slice) has been removed; the
// public symbols below are thin re-export shims so existing consumers keep
// compiling unchanged.
// ---------------------------------------------------------------------------

/**
 * Scribia's user shape. Display name is intentionally NOT decoded from the JWT —
 * the Foundation backend dropped the `name` claim. It is sourced from `/auth/me`
 * (decrypted server-side) via the `me` slice instead.
 */
export interface ScribiaUser {
  id: string;
  email: string;
  role: string;
  tenantId: string;
  permissions: string[];
  hasPhoneNumber: boolean;
}

function decodeUser(token: string): ScribiaUser {
  const p = JSON.parse(
    atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
  );
  return {
    id: p.sub ?? '',
    email: p.email ?? '',
    role: p.role ?? '',
    tenantId: p.tenant_id ?? '',
    permissions: Array.isArray(p.permissions) ? p.permissions : [],
    hasPhoneNumber: !!p.has_phone_number,
  };
}

export const scribiaAuth = createAuthSlice<ScribiaUser>({
  refreshUrl: `${env.api.baseUrl}/auth/refresh`,
  logoutUrl: `${env.api.baseUrl}/auth/logout`,
  decodeUser,
});

// ---------------------------------------------------------------------------
// Public shims — keep LoginPage / SsoCallbackPage / AuthManager / Header /
// api/client compiling against the same import surface as before.
// ---------------------------------------------------------------------------

export const { applyTokens, setAuthLoading } = scribiaAuth.actions;
export const { refreshToken, logoutUser } = scribiaAuth.thunks;
export const tokenReceived = scribiaAuth.tokenReceived;

export const getTokenExpiry = foundationGetTokenExpiry;

export const {
  selectAccessToken,
  selectAuthUser,
  selectIsAuthenticated,
  selectIsAuthLoading,
} = scribiaAuth.selectors;

export default scribiaAuth.reducer;
